import React, { useCallback, useEffect, useRef, useState } from 'react'
import { isEmpty } from 'ramda'
import PropTypes from 'prop-types'
import { useIntl } from 'react-intl'
import { useDispatch } from 'react-redux'
import CloseIcon from '@material-ui/icons/Close'
import { useMutation } from '@tanstack/react-query'
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline'
import {
  Dialog,
  IconButton,
  Typography,
  DialogActions,
  DialogContent,
} from '@material-ui/core'

import effects from 'app/effects/effects'
import { CANCEL } from 'app/constants/button'
import { PHOTO_GALLERY_LIMIT } from 'libs/file'
import EmptyState from 'app/componentV4/EmptyState'
import MessageAlert from 'app/components/MessageAlert'
import SubmitDialog from 'app/componentV4/Dialog/SubmitDialog'
import OutlinedButtons from 'app/componentV4/Button/OutlinedButtons'

import { ReactComponent as EmptyImageIcon } from 'app/components/icons/empty-image-icon.svg'

import { Photo } from './Photo'
import UploadBox from './UploadBox'

const PhotoUploadDialog = ({ images, isOpen, onClose }) => {
  const dispatch = useDispatch()
  const { formatMessage } = useIntl()

  const cancellableRef = useRef(new AbortController())

  const [photos, setPhotos] = useState([])
  const [photoCounting, setPhotoCounting] = useState(0)
  const [isDialogOpen, setDialogOpen] = useState(false)
  const [isDialogExit, setIsDialogExit] = useState(false)
  const [photoLoading, setPhotoLoading] = useState(false)

  const deletePhoto = useMutation({
    mutationFn: async (id) => {
      const response = await effects.apis.profileInfo.deletePhoto(
        {
          photoIds: [id],
        },
        cancellableRef.current.signal,
      )
      return response.data
    },
    onSuccess: (res, id) => {
      setPhotos((prevs) =>
        prevs.map((photo) =>
          photo.id === id
            ? { ...photo, isHidden: true, isDeleting: false }
            : photo,
        ),
      )
      dispatch(
        MessageAlert.showSuccess({
          message: formatMessage({ id: 'image.deleted.label' }),
        }),
      )
    },
    onError: (error, id) => {
      if (error.code !== 'ERR_CANCELED') {
        setPhotos((prevs) =>
          prevs.map((photo) =>
            photo.id === id ? { ...photo, isDeleting: false } : photo,
          ),
        )
      }
      const msgError = error.response.data
      dispatch(MessageAlert.showNetworkError(formatMessage)(msgError))
    },
  })

  const deleteAll = useMutation({
    mutationFn: async () => {
      const response = await effects.apis.profileInfo.deletePhoto(
        {
          photoIds: photos.map((item) => item.id),
        },
        cancellableRef.current.signal,
      )
      return response.data
    },
    onSuccess: () => {
      setPhotos([])
      setDialogOpen(false)
      dispatch(
        MessageAlert.showSuccess({
          message: formatMessage({ id: 'image.deleted.label' }),
        }),
      )
    },
    onError: (error) => {
      const msgError = error.response.data
      dispatch(MessageAlert.showNetworkError(formatMessage)(msgError))
    },
  })

  const handleDeletePhoto = useCallback(
    async (id) => {
      setPhotos((prevs) =>
        prevs.map((photo) =>
          photo.id === id ? { ...photo, isDeleting: true } : photo,
        ),
      )

      await deletePhoto.mutate(id)
    },
    [deletePhoto],
  )

  const onUploadPush = useCallback(
    async (filesEncoded) => {
      const countFile = filesEncoded.length
      const emptyPhoto = photoCounting === 0

      // Case: Already uploaded 49 images, user tries to upload 2 more images. Do not allow the additional 2.
      if (!emptyPhoto && countFile + photoCounting > PHOTO_GALLERY_LIMIT)
        return dispatch(
          MessageAlert.showWarning({
            message:
              'You have reached the image upload limit. Please delete some images to add new ones.',
          }),
        )

      // Case: If a bulk upload exceeds the max limit, do not allow the upload and show a toast:
      if (emptyPhoto && countFile > PHOTO_GALLERY_LIMIT)
        return dispatch(
          MessageAlert.showWarning({
            message: `You can upload up to ${PHOTO_GALLERY_LIMIT} images. Please reduce your selection.`,
          }),
        )

      const filesToAdd = filesEncoded.map((fileEncoded) => ({
        uploadId: Date.now() + Math.random(),
        source: 'manual',
        url: fileEncoded,
        percentage: 0,
        isLoading: true,
      }))
      setPhotos([...photos, ...filesToAdd])
    },
    [dispatch, photoCounting, photos],
  )

  const handleClose = useCallback(() => {
    if (photoLoading) {
      setIsDialogExit(true)
    } else {
      onClose()
    }
  }, [onClose, photoLoading])

  const onUploadSuccess = useCallback((id, uploadId) => {
    //set Id if upload image success
    setPhotos((prevs) =>
      prevs.map((photo) =>
        photo.uploadId === uploadId
          ? { ...photo, id: id, isLoading: false }
          : photo,
      ),
    )
  }, [])

  const onLoading = useCallback(
    (uploadId, isLoading) =>
      //set isLoading if image uploading
      setPhotos((prevs) =>
        prevs.map((photo) =>
          photo.uploadId === uploadId
            ? { ...photo, isLoading: isLoading }
            : photo,
        ),
      ),
    [],
  )

  const onExit = useCallback(() => {
    //set isCancel if Exit then upload image gonna cancel
    setPhotos((prevs) =>
      prevs.map((photo) => {
        return { ...photo, isCancel: true }
      }),
    )
    setIsDialogExit(false)
    onClose()
  }, [onClose])

  useEffect(() => {
    setPhotos(images)
  }, [images])

  useEffect(() => {
    setPhotoLoading(photos.some((photo) => photo.isLoading && !photo.isHidden))
    setPhotoCounting(photos.filter((photo) => !photo.isHidden).length)
  }, [photos])

  return (
    <>
      <Dialog
        open={isOpen}
        onClose={() => handleClose()}
        fullWidth
        maxWidth="md"
        PaperProps={{
          style: { height: '750px', borderRadius: 16, padding: '3rem' },
        }}
      >
        <div className="flex justify-between">
          <Typography className="text-16 font-500 text-primary items-center flex">
            {`${formatMessage({
              id: 'profile.info.photo',
            })} (${photoCounting})`}
          </Typography>
          <IconButton
            aria-label="close-opening-hours-dialog"
            onClick={() => handleClose()}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>
        <DialogContent>
          <UploadBox onUploadPush={onUploadPush} counting={photoCounting} />

          {photoCounting === 0 ? (
            <EmptyState
              isStyle={false}
              icon={<EmptyImageIcon />}
              title={formatMessage({
                id: 'profile.photo.gallery.dialog.empty.title',
              })}
              description={formatMessage({
                id: 'profile.photo.gallery.dialog.empty.description',
              })}
            />
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {photos.map((photo, index) => (
                <>
                  {!photo.isHidden && (
                    <div
                      key={index}
                      className="mx-auto"
                      style={{ order: photoCounting - index }}
                    >
                      <Photo
                        img={photo}
                        alt={`Photo Gallery ${photoCounting - index}`}
                        onDelete={(id) => handleDeletePhoto(id)}
                        onReject={(uploadId) => {
                          setPhotos((prevs) =>
                            prevs.map((photo) => {
                              if (photo.uploadId === uploadId)
                                return { ...photo, isHidden: true }
                              else return photo
                            }),
                          )
                        }}
                        onUploadSuccess={onUploadSuccess}
                        onLoading={onLoading}
                      />
                    </div>
                  )}
                </>
              ))}
            </div>
          )}
        </DialogContent>
        <DialogActions>
          {photoCounting !== 0 && !photoLoading && (
            <div className="flex justify-end">
              <OutlinedButtons
                color={CANCEL}
                onClick={() => setDialogOpen(true)}
              >
                <DeleteOutlineIcon fontSize="small" className="text-red-500" />
                {formatMessage({
                  id: 'profile.info.delete.all',
                })}
              </OutlinedButtons>
            </div>
          )}
        </DialogActions>
      </Dialog>
      <SubmitDialog
        id="dialog-confirm-place-public"
        open={isDialogOpen}
        type={CANCEL}
        title={formatMessage({
          id: 'profile.dialog.confirm.place.public.title',
        })}
        submitText={formatMessage({ id: 'action.delete' })}
        closeText={formatMessage({ id: 'text.close' })}
        onSubmit={() => deleteAll.mutate()}
        description={formatMessage({
          id: 'profile.dialog.confirm.place.public.description',
        })}
        onClose={() => {
          setDialogOpen(false)
        }}
        isLoading={deleteAll.isPending}
        disabledSubmit={deleteAll.isPending}
      />
      <SubmitDialog
        id="dialog-confirm-exit-upload"
        open={isDialogExit}
        type={CANCEL}
        title={formatMessage({ id: 'ai.dialog.exist.dialog.title' })}
        submitText={formatMessage({
          id: 'profile.dialog.confirm.exit.upload.submit.text',
        })}
        closeText={formatMessage({ id: 'action.cancel' })}
        onSubmit={() => {
          onExit()
        }}
        description={formatMessage({
          id: 'profile.dialog.confirm.exit.upload.description',
        })}
        onClose={() => {
          setIsDialogExit(false)
        }}
      />
    </>
  )
}

PhotoUploadDialog.propTypes = {
  images: PropTypes.arrayOf(
    PropTypes.shape({ url: PropTypes.string.isRequired }),
  ),
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
}

export default PhotoUploadDialog
